import FormShell from "../components/FormShell";

const RED = "#E82529";

// Exact v7 minimal line-icons
function TravelerIcon({ type, color = RED, size = 30 }) {
  const s = { display: "block", width: size, height: size };
  if (type === "solo") return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="10" r="5" stroke={color} strokeWidth="1.8" />
      <path d="M6 28c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
  if (type === "couple") return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="11" cy="10" r="4" stroke={color} strokeWidth="1.8" />
      <path d="M3 27c0-4.4 3.6-8 8-8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="21" cy="10" r="4" stroke={color} strokeWidth="1.8" />
      <path d="M29 27c0-4.4-3.6-8-8-8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M15 19c1.8-.6 3.6-.6 5 0" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
  if (type === "family_with_kids") return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="10" cy="9" r="4" stroke={color} strokeWidth="1.8" />
      <circle cx="22" cy="9" r="4" stroke={color} strokeWidth="1.8" />
      <circle cx="16" cy="19" r="3" stroke={color} strokeWidth="1.6" />
      <path d="M3 28c0-4 3.1-7 7-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M29 28c0-4-3.1-7-7-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M13 27c0-1.7 1.3-3 3-3s3 1.3 3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
  if (type === "family_adults") return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="8" cy="9" r="3.5" stroke={color} strokeWidth="1.8" />
      <circle cx="16" cy="9" r="3.5" stroke={color} strokeWidth="1.8" />
      <circle cx="24" cy="9" r="3.5" stroke={color} strokeWidth="1.8" />
      <path d="M2 27c0-3.9 2.7-7 6-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M30 27c0-3.9-2.7-7-6-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 21c1.7-1 3.6-1.5 6-1.5s4.3.5 6 1.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
  if (type === "friends") return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="7" cy="10" r="3.2" stroke={color} strokeWidth="1.7" />
      <circle cx="16" cy="8" r="3.8" stroke={color} strokeWidth="1.8" />
      <circle cx="25" cy="10" r="3.2" stroke={color} strokeWidth="1.7" />
      <path d="M1 27c0-3.3 2.5-6 6-6" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
      <path d="M31 27c0-3.3-2.5-6-6-6" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
      <path d="M10 22c1.7-1.2 3.7-2 6-2s4.3.8 6 2" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
  // honeymoon — interlocked rings
  return (
    <svg style={s} viewBox="0 0 32 32" fill="none">
      <circle cx="12" cy="16" r="7" stroke={color} strokeWidth="1.8" />
      <circle cx="20" cy="16" r="7" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

const TRAVELER_OPTIONS = [
  { value: "solo", label: "Solo" },
  { value: "couple", label: "Couple" },
  { value: "family_with_kids", label: "Family with kids" },
  { value: "family_adults", label: "Family — adults" },
  { value: "friends", label: "Friends" },
  { value: "honeymoon", label: "Honeymoon" },
];

const KID_AGE_OPTIONS = [
  { value: "under_5", label: "Under 5" },
  { value: "5_12", label: "5–12" },
  { value: "teens", label: "Teens" },
];

const chip = (selected) => ({
  padding: "8px 18px", borderRadius: 999, cursor: "pointer",
  fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 500, fontSize: 13,
  background: selected ? RED : "var(--zen-s1)",
  color: selected ? "#fff" : "var(--zen-text)",
  border: `1.5px solid ${selected ? RED : "var(--zen-border-h)"}`,
});

export default function TravelerPage({
  onNext, onBack, startOver,
  travelerType, setTravelerType,
  kidAges, setKidAges,
  isFirstTimer, setIsFirstTimer,
}) {
  const canContinue = travelerType && isFirstTimer !== null;
  const toggleKidAge = (age) => setKidAges(prev => prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]);

  return (
    <FormShell
      step={3}
      photo="/photos/onboard-traveler"
      photoPos="0% 47%"
      bigWord={"WHO'S\nGOING?"}
      leftSubtitle="This helps us pick the right places and pace for your group"
      title="Who's going?"
      uppercaseTitle={false}
      subtitle="This helps us pick the right places and pace for your group"
      onBack={onBack}
      onStartOver={startOver}
      onContinue={onNext}
      continueDisabled={!canContinue}
    >
      {/* Traveler grid */}
      <div className="grid grid-cols-2 gap-2.5">
        {TRAVELER_OPTIONS.map(opt => {
          const sel = travelerType === opt.value;
          return (
            <button key={opt.value}
              onClick={() => { setTravelerType(opt.value); if (opt.value !== "family_with_kids") setKidAges([]); }}
              className="text-left transition-all duration-200"
              style={{
                padding: "16px 14px", borderRadius: 4, cursor: "pointer",
                background: sel ? "var(--zen-s2)" : "var(--zen-s1)",
                border: `2px solid ${sel ? RED : "var(--zen-border)"}`,
                boxShadow: sel ? `0 0 0 1px ${RED}` : "none",
              }}>
              <div style={{ marginBottom: 10 }}>
                <TravelerIcon type={opt.value} color={sel ? RED : "var(--zen-sub)"} size={30} />
              </div>
              <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: sel ? RED : "var(--zen-text)" }}>
                {opt.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Kid ages */}
      {travelerType === "family_with_kids" && (
        <div>
          <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: "var(--zen-text)", marginBottom: 10 }}>
            Ages of kids?
          </div>
          <div className="flex gap-2">
            {KID_AGE_OPTIONS.map(age => (
              <button key={age.value} onClick={() => toggleKidAge(age.value)} style={chip(kidAges.includes(age.value))}>
                {age.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Been before */}
      <div>
        <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: "var(--zen-text)", marginBottom: 10 }}>
          Been to Japan before?
        </div>
        <div className="flex gap-2.5">
          {[{ value: true, label: "First time" }, { value: false, label: "Been before" }].map(opt => {
            const sel = isFirstTimer === opt.value;
            return (
              <button key={String(opt.value)} onClick={() => setIsFirstTimer(opt.value)}
                style={{
                  flex: 1, padding: "12px 0", borderRadius: 4, textAlign: "center", cursor: "pointer",
                  fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 500, fontSize: 13,
                  background: sel ? RED : "var(--zen-s1)", color: sel ? "#fff" : "var(--zen-text)",
                  border: `1.5px solid ${sel ? RED : "var(--zen-border-h)"}`,
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </FormShell>
  );
}
